import React, {
  useState,
  useCallback,
  useEffect,
  forwardRef,
  ReactElement,
  Ref,
  memo,
} from "react";
import {
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import {
  Button as ButtonAntd,
  ConfigProvider,
  Input,
  Popconfirm,
  Space,
  Tooltip,
  message,
} from "antd";

import {
  CloseOutlined,
  DeleteFilled,
  MailFilled,
  PlusCircleOutlined,
  SendOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { COLOR_PRIMARY } from "../../constants";
import { v4 as uuidV4 } from "uuid";
import { useWidth } from "../../hooks";
import { sendReport } from "@apis";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type document = "invoice-sri" | "invoice-sri-xml" | "invoice" | "quotation";
type module = "issued" | "received" | "sale" | "credit-note" | "quotation";

interface ModalSendReportPropsI {
  id: string;
  document: document;
  module: module;
  initialPhones?: string[];
  initialMails?: string[];
  tooltip?: string;
  icon?: ReactElement;
}

interface ValuesI {
  uuid: string;
  value: string;
}

const toValues = (values?: string[]): ValuesI[] =>
  (values ?? []).filter(Boolean).map((value) => ({ uuid: uuidV4(), value }));

export const SendReport = ({
  document,
  module,
  id,
  initialPhones,
  initialMails,
  tooltip,
  icon,
}: ModalSendReportPropsI) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip arrow={false} title={tooltip ?? "Enviar"}>
        <ButtonAntd
          type="dashed"
          size="small"
          onClick={() => setOpen(true)}
          icon={icon ?? <SendOutlined />}
        />
      </Tooltip>
      <ModalReport
        open={open}
        id={id}
        handleClose={() => setOpen(false)}
        document={document}
        module={module}
        initialPhones={initialPhones}
        initialMails={initialMails}
      />
    </>
  );
};

export interface SendReportPropsI {
  id: string;
  open: boolean;
  handleClose: () => void;
  document: document;
  module: module;
  initialPhones?: string[];
  initialMails?: string[];
}

export const ModalReport = ({
  open,
  id,
  handleClose,
  document,
  module,
  initialPhones,
  initialMails,
}: SendReportPropsI) => {
  const width = useWidth();
  const [messageApi, contextHolder] = message.useMessage();
  const [receivers, setReceivers] = useState<ValuesI[]>(() =>
    toValues(initialMails),
  );
  const [receiversNum, setReceiversNum] = useState<ValuesI[]>(() =>
    toValues(initialPhones),
  );
  const [isSending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReceivers(toValues(initialMails));
    setReceiversNum(toValues(initialPhones));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSendReport = async () => {
    setSending(true);
    try {
      const mails = receivers
        .filter((el) => el.value.trim().length > 9)
        .map((el) => el.value);
      const phones = receiversNum
        .filter((el) => el.value.trim().length > 9)
        .map((el) => el.value);

      messageApi.loading({ key: "sending", content: "Enviando Reporte 📑" });

      const { data } = await sendReport({
        document,
        module,
        id,
        mails,
        phones,
      });
      if (!data) {
        message.error("Error al enviar, comuníquese con el administrador");
        return;
      }

      if (data.mailsFailed.length > 0) {
        messageApi.warning({
          key: "sending",
          content: `Enviado con ${data.mailsFailed.length} error(es). Revisa los correos fallidos.`,
          duration: 4,
        });
      } else {
        messageApi.success({
          key: "sending",
          content: "Enviado con éxito",
          duration: 2,
        });
      }

      if (data.phonesFailed.length > 0) {
        messageApi.info({
          content: `Enviado con ${data.phonesFailed.length} error(es). Revisa los correos fallidos.`,
          duration: 4,
        });
      }
    } catch (error: any) {
      messageApi.error({
        key: "sending",
        content:
          error.message ?? "Error al enviar, comuníquese con el administrador",
      });
    } finally {
      setSending(false);
    }
  };

  const handleAddPhone = useCallback(() => {
    const obj = {
      uuid: uuidV4(),
      value: "",
    };
    receiversNum.push(obj);
    setReceiversNum([...receiversNum]);
  }, [receiversNum]);

  const handleUpdatePhone = useCallback(
    (uuid: string, value: string) => {
      setReceiversNum((rec) =>
        rec.map((el) => {
          if (el.uuid === uuid) return { ...el, value };
          return el;
        }),
      );
    },
    [receiversNum],
  );

  const handleDeletePhone = useCallback(
    (uuid: string) => {
      setReceiversNum((rec) => rec.filter((el) => el.uuid !== uuid));
    },
    [receiversNum],
  );

  const handleAddMail = useCallback(() => {
    const obj = {
      uuid: uuidV4(),
      value: "",
    };
    receivers.push(obj);
    setReceivers([...receivers]);
  }, [receivers]);

  const handleUpdateMail = useCallback(
    (uuid: string, value: string) => {
      setReceivers((rec) =>
        rec.map((el) => {
          if (el.uuid === uuid) return { ...el, value };
          return el;
        }),
      );
    },
    [receivers],
  );

  const handleDeleteMail = useCallback(
    (uuid: string) => {
      setReceivers((rec) => rec.filter((el) => el.uuid !== uuid));
    },
    [receivers],
  );

  return (
    <>
      {contextHolder}
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        fullScreen={width === "xs"}
        maxWidth="sm"
        fullWidth
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-send-report"
      >
        <DialogTitle
          sx={{
            bgcolor: COLOR_PRIMARY,
            color: "white",
            position: "relative",
            fontWeight: "bolder",
          }}
        >
          {"🟢 Envío de Documentos "}
          <ButtonAntd
            type="primary"
            danger
            style={{ position: "absolute", right: 5, top: 10 }}
            onClick={handleClose}
            icon={<CloseOutlined />}
          />
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: "relative" }}>
          <Grid container spacing={0}>
            <Grid
              item
              xs={12}
              sm={6}
              sx={{ p: 1, bgcolor: "aliceblue", borderRadius: 1 }}
            >
              <Grid
                container
                spacing={1}
                alignContent="flex-start"
                alignItems="flex-start"
                sx={{
                  minHeight: 300,
                  maxHeight: 300,
                  height: 300,
                  overflowY: "auto",
                }}
              >
                <Grid
                  item
                  xs={12}
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 600,
                    bgcolor: "aliceblue",
                  }}
                >
                  <Space.Compact style={{ width: "100%" }}>
                    <ButtonAntd
                      style={{ width: "50%" }}
                      type="dashed"
                      onClick={handleAddPhone}
                      icon={<WhatsAppOutlined style={{ color: "green" }} />}
                    >
                      Celulares
                    </ButtonAntd>
                    <ButtonAntd
                      style={{ width: "50%" }}
                      onClick={handleAddPhone}
                      icon={<PlusCircleOutlined />}
                    >
                      Agregar
                    </ButtonAntd>
                  </Space.Compact>
                </Grid>
                {receiversNum.map((el) => (
                  <Grid item xs={12} key={el.uuid}>
                    <ItemComponent
                      onUpdate={handleUpdatePhone}
                      onDelete={handleDeletePhone}
                      placeholder="0988776655"
                      uuid={el.uuid}
                      name="Celular"
                      value={el.value}
                      maxLength={10}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              sx={{ p: 1, bgcolor: "ghostwhite", borderRadius: 1 }}
            >
              <Grid
                container
                spacing={1}
                alignContent="flex-start"
                alignItems="flex-start"
                sx={{
                  minHeight: 300,
                  maxHeight: 300,
                  height: 300,
                  overflowY: "auto",
                }}
              >
                <Grid
                  item
                  xs={12}
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 600,
                    bgcolor: "ghostwhite",
                  }}
                >
                  <Space.Compact style={{ width: "100%" }}>
                    <ButtonAntd
                      style={{ width: "50%" }}
                      type="dashed"
                      onClick={handleAddMail}
                      icon={<MailFilled style={{ color: "blue" }} />}
                    >
                      Correos
                    </ButtonAntd>
                    <ButtonAntd
                      style={{ width: "50%" }}
                      onClick={handleAddMail}
                      icon={<PlusCircleOutlined />}
                    >
                      Agregar
                    </ButtonAntd>
                  </Space.Compact>
                </Grid>
                {receivers.map((el) => (
                  <Grid item xs={12} key={el.uuid}>
                    <ItemComponent
                      onUpdate={handleUpdateMail}
                      onDelete={handleDeleteMail}
                      placeholder="correo@mail.com"
                      uuid={el.uuid}
                      name="Correo"
                      value={el.value}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px dashed gainsboro" }}>
          <ButtonAntd
            type="dashed"
            danger
            onClick={handleClose}
            icon={<CloseOutlined />}
          >
            Cancelar
          </ButtonAntd>
          <ConfigProvider
            theme={{
              components: {
                Popconfirm: {
                  zIndexPopup: 1301,
                },
              },
            }}
          >
            <Popconfirm
              title="Enviar Reporte"
              description="Información Correcta?"
              onConfirm={handleSendReport}
              okText="Si"
              cancelText="No"
            >
              <ButtonAntd
                type="primary"
                icon={<SendOutlined />}
                // loading={isSending}
                disabled={isSending}
              >
                {isSending ? "Espere" : "Enviar"}
              </ButtonAntd>
            </Popconfirm>
          </ConfigProvider>
        </DialogActions>
      </Dialog>
    </>
  );
};

type names = "Celular" | "Correo";

interface ItemProps {
  uuid: string;
  onDelete: (uuid: string) => void;
  onUpdate: (uuid: string, value: string) => void;
  name: names;
  placeholder: string;
  value: string;
  maxLength?: number;
}

const ItemComponent = memo(
  ({
    onDelete,
    name,
    onUpdate,
    placeholder,
    uuid,
    value: elvalue,
    maxLength,
  }: ItemProps) => {
    const [value, setValue] = useState(elvalue);

    return (
      <ConfigProvider
        theme={{
          components: {
            Input: {
              inputFontSize: 12,
            },
          },
        }}
      >
        <Input
          addonBefore={name}
          value={value}
          maxLength={maxLength ? maxLength : 1000}
          onChange={(e) => setValue(e.target.value)}
          onBlur={(e) => {
            onUpdate(uuid, value?.trim() ?? "");
            setValue(e.target.value?.trim() ?? "");
          }}
          placeholder={placeholder}
          style={{ width: "100%", fontSize: "10px" }}
          status={value.length < 10 ? "error" : ""}
          suffix={
            <ButtonAntd
              danger
              size="small"
              onClick={() => onDelete(uuid)}
              icon={<DeleteFilled />}
            />
          }
        />
      </ConfigProvider>
    );
  },
);
